<template>
  <div class="nested-tabs-container">
    <div class="nested-tabs-sidebar">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['nested-tab-btn', { active: activeTab === tab.id }]"
        @click="activeTab = tab.id"
      >
        <i v-if="tab.icon" :class="tab.icon"></i>
        <span>{{ tab.title }}</span>
      </button>
    </div>
    <div class="nested-tabs-content">
      <transition name="fade" mode="out-in">
        <div
          v-for="tab in tabs"
          v-show="activeTab === tab.id"
          :key="tab.id"
          class="nested-tab-panel"
        >
          <!-- 2x2 Card Grid Layout -->
          <div v-if="tab.type === 'card-grid'" class="card-grid-2x2">
            <div v-for="(card, index) in tab.cards" :key="index" class="grid-card">
              <div class="card-icon-wrapper">
                <i v-if="card.icon" :class="card.icon"></i>
              </div>
              <h3 class="card-title">{{ card.title }}</h3>
              <p class="card-description">{{ card.description }}</p>
              <ul v-if="card.items" class="card-items">
                <li v-for="(item, idx) in card.items" :key="idx">{{ item }}</li>
              </ul>
            </div>
          </div>

          <!-- Horizontal Tool Badges -->
          <div v-else-if="tab.type === 'tool-badges'" class="tool-badges-container">
            <div v-for="(category, index) in tab.categories" :key="index" class="tool-category">
              <h4 v-if="category.name" class="category-name">{{ category.name }}</h4>
              <div class="tools-row">
                <span v-for="(tool, idx) in category.tools" :key="idx" class="tool-badge">
                  <i v-if="tool.icon" :class="tool.icon"></i>
                  <span>{{ tool.name }}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Project Content -->
          <ProjectTabContent v-else-if="tab.type === 'project'" :project="tab.project" />

          <!-- Legacy HTML Content -->
          <div v-else v-html="tab.content"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import ProjectTabContent from './ProjectTabContent.vue'

interface Card {
  icon?: string
  title: string
  description: string
  items?: string[]
}

interface Tool {
  icon?: string
  name: string
}

interface ToolCategory {
  name?: string
  tools: Tool[]
}

interface Tech {
  logo?: string
  icon?: string
  name: string
}

interface Feature {
  icon: string
  title: string
  description: string
}

interface Project {
  icon: string
  title: string
  tagline: string
  description: string
  buildItems: string[]
  technologies: Tech[]
  features: Feature[]
  duration: string
  difficulty: string
  prerequisites: string
}

interface Tab {
  id: string
  title: string
  icon?: string
  type?: 'card-grid' | 'tool-badges' | 'project' | 'html'
  content?: string
  cards?: Card[]
  categories?: ToolCategory[]
  project?: Project
}

const props = defineProps<{
  tabs: Tab[]
  defaultTab?: string
}>()

const activeTab = ref(props.defaultTab || props.tabs[0]?.id || '')
</script>

<style scoped>
.nested-tabs-container {
  display: flex;
  gap: 24px;
  margin: 24px 0;
}

.nested-tabs-sidebar {
  flex-shrink: 0;
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nested-tab-btn {
  width: 100%;
  padding: 16px 20px;
  border: none;
  background: white;
  color: #64748B;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  text-align: left;
  border: 2px solid rgba(139, 92, 246, 0.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.nested-tab-btn:hover {
  background: rgba(139, 92, 246, 0.05);
  border-color: rgba(139, 92, 246, 0.3);
  transform: translateX(4px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.15);
}

.nested-tab-btn.active {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  transform: translateX(4px);
}

.nested-tab-btn i {
  font-size: 18px;
  min-width: 20px;
}

.nested-tabs-content {
  flex: 1;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  padding: 24px;
  min-height: 400px;
}

.nested-tab-panel {
  animation: fadeIn 0.3s ease;
}

/* 2x2 Card Grid */
.card-grid-2x2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.grid-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.1);
  transition: all 0.3s ease;
}

.grid-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.3);
}

.card-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.card-icon-wrapper i {
  font-size: 20px;
  color: white;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.card-description {
  font-size: 13px;
  color: #64748B;
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.card-items {
  list-style: none;
  padding: 0;
  margin: 0;
}

.card-items li {
  font-size: 12px;
  color: #475569;
  padding: 5px 0;
  padding-left: 18px;
  position: relative;
  line-height: 1.4;
}

.card-items li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
  font-weight: bold;
  font-size: 14px;
}

/* Tool Badges */
.tool-badges-container {
  padding: 8px 0;
}

.tool-category {
  margin-bottom: 24px;
}

.tool-category:last-child {
  margin-bottom: 0;
}

.category-name {
  font-size: 14px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}

.tools-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tool-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  transition: all 0.3s ease;
  cursor: default;
}

.tool-badge:hover {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(168, 85, 247, 0.05));
  border-color: rgba(139, 92, 246, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.15);
}

.tool-badge i {
  font-size: 16px;
  color: #3b82f6;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 968px) {
  .nested-tabs-container {
    flex-direction: column;
  }

  .nested-tabs-sidebar {
    width: 100%;
    flex-direction: row;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 8px;
  }

  .nested-tab-btn {
    min-width: 180px;
    padding: 12px 16px;
    font-size: 14px;
  }

  .nested-tab-btn i {
    font-size: 16px;
  }

  .card-grid-2x2 {
    grid-template-columns: 1fr;
  }
}
</style>
